/**
 * Redux store setup with saga middleware.
 * Core sagas are always forked. Plugin sagas are dynamically forked/cancelled
 * via runPluginSaga/cancelPluginSaga when plugins are enabled/disabled.
 */

import { configureStore } from '@reduxjs/toolkit';
import createSagaMiddleware, { Task } from 'redux-saga';
import { rootReducer, addAsyncReducer, removeAsyncReducer, hasAsyncReducer } from './root-reducer';
import { authSaga } from '@/features/auth/sagas';
import { todosSaga } from '@/features/todos/sagas';
import { calendarSaga } from '@/features/calendar/sagas';
import { calendarConnectionsSaga } from '@/features/calendar-connections';
import { householdSaga } from '@/features/household/sagas';
import { notificationsSaga } from '@/features/notifications/sagas';
import { pluginsToggleSaga } from '@/features/plugins-toggle/sagas';
import { adminSystemInfoSaga } from '@/features/admin-system-info/sagas';
import { predicateSaga, predicateBoot } from '@/features/predicate/sagas';
import { wsRootSaga, registerWsPlugin } from '@/infra/ws/root-saga';
import { todosWsPlugin } from '@/features/todos/ws-plugin';
import { backgroundTasksSaga } from '@/features/background-tasks/sagas';
import { backgroundTasksWsPlugin } from '@/features/background-tasks/ws-plugin';
import { predicateWsPlugin } from '@/features/predicate/ws-plugin';
import { tabFocusSaga } from '@/infra/tab-focus/saga';
import { syncCoordinatorSaga } from '@/infra/sync/saga';
import { resumeDetectorSaga } from '@/infra/sync/resume-detector';
import { getFlowRef } from '@/features/qpl-client';
import { FLOW_REF_CONTEXT_KEY } from '@/features/qpl-client/saga-helpers';
import { all, fork, setContext } from 'redux-saga/effects';

// Register feature WS plugins before rootSaga starts consuming messages.
// Each feature owns its message-type → action mapping in its own module.
registerWsPlugin(todosWsPlugin);
registerWsPlugin(backgroundTasksWsPlugin);
registerWsPlugin(predicateWsPlugin);

// Root saga — forks only core feature sagas (not plugins)
export function* rootSaga() {
  // Install the QPL flow ref into saga context once. All child sagas
  // (spawned here and recursively from their watchers) inherit this
  // context, so `withSagaFlow` / `emitSagaPoint` can read the current
  // flow id without touching a module global. The ref is mutable —
  // runInFlow / withSagaFlow update it as gestures begin and end.
  yield setContext({ [FLOW_REF_CONTEXT_KEY]: getFlowRef() });
  yield all([
    fork(authSaga),
    fork(todosSaga),
    fork(calendarSaga),
    fork(calendarConnectionsSaga),
    fork(householdSaga),
    fork(notificationsSaga),
    fork(pluginsToggleSaga),
    fork(adminSystemInfoSaga),
    fork(backgroundTasksSaga),
    fork(wsRootSaga),
    fork(tabFocusSaga),
    fork(predicateSaga),
    // Owns background-sync cadence: turns WS reconnects, tab refocuses
    // and process resumes into one gated `sync/refresh` cycle.
    fork(syncCoordinatorSaga),
    fork(resumeDetectorSaga),
  ]);
}

// Saga middleware
const sagaMiddleware = createSagaMiddleware();

// Store
export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({ thunk: false }).concat(sagaMiddleware),
});

// Run core sagas
sagaMiddleware.run(rootSaga);

// The first predicate-rules pull fires from the predicate saga's
// `auth/loginSuccess` watcher, not here — dispatching at module
// init races foauth's silent refresh and the API call goes out
// without a Bearer header. Subsequent ticks come from the saga's
// 5-minute poll loop and from WS reconnect.
void predicateBoot;

// ─── Dynamic Plugin Saga Management ───────────────────────

const runningPluginSagas: Map<string, Task> = new Map();

/**
 * Dynamically fork a plugin saga. Tracks the task for later cancellation.
 */
export function runPluginSaga(name: string, saga: () => Generator): void {
  if (runningPluginSagas.has(name)) {
    return; // Already running — idempotent
  }
  const task = sagaMiddleware.run(saga);
  runningPluginSagas.set(name, task);
}

/**
 * Cancel a running plugin saga by name.
 */
export function cancelPluginSaga(name: string): void {
  const task = runningPluginSagas.get(name);
  if (task) {
    task.cancel();
    runningPluginSagas.delete(name);
  }
}

/**
 * Check whether a plugin saga is currently running.
 */
export function isPluginSagaRunning(name: string): boolean {
  return runningPluginSagas.has(name);
}

// ─── Plugin Lifecycle (reducer + saga together) ───────────

/**
 * Load a plugin: mount its reducer and fork its saga.
 */
export function loadPlugin(
  name: string,
  reducer?: (state: any, action: any) => any,
  saga?: () => Generator
): void {
  if (reducer) {
    addAsyncReducer(name, reducer, store.dispatch);
  }
  if (saga) {
    runPluginSaga(name, saga);
  }
}

/**
 * Unload a plugin: cancel its saga and remove its reducer.
 */
export function unloadPlugin(name: string): void {
  cancelPluginSaga(name);
  if (hasAsyncReducer(name)) {
    removeAsyncReducer(name, store.dispatch);
  }
}

export type { RootState, AppDispatch } from './types';
